import {
	Observable, catchError, of, tap,
} from 'rxjs';

import { Container, Service } from '../../../../symphony';

import { SlDataService } from '../sl-data.service';
import {
	SlAIChatContext,
	aIChatPagePath,
	initialAssistantMessages,
	limitError,
	testUserHint,
} from '../../global-constants';
import { ChatViewState, Role } from '../../global-interface';
import { SlApiService } from '../sl-api.service';
import { SlTrackingService } from './sl-tracking.service';
import { IAIAssistantResponse, Ratings } from '../../../../api/public/ai-chat-api';

@Service()
export class SlAIChatService {
	private apiService = Container.take(SlAIChatContext, SlApiService);

	private dataService = Container.take(SlAIChatContext, SlDataService);

	private trackingService = Container.take(
		SlAIChatContext,
		SlTrackingService,
	);

	private addMessageToChatHistory = (
		message: string,
		role: Role,
		aliases?: string[],
		id?: string,
		usage?: IAIAssistantResponse['usage'],
	) => {
		// Check if is test user only on first message if the message contains test user hint
		if (!this.dataService.conversationId$.value) {
			const isTestUser = message.includes(testUserHint);
			this.dataService.isTestUser$.next(isTestUser);
		}

		this.dataService.messages$.next([
			...this.dataService.messages$.value,
			{
				message: message.replace(testUserHint, ''),
				role,
				usage,
				id,
				aliases,
			},
		]);
	};

	private setCourseImages = async () => {
		this.apiService
			.getLearningExperiences()
			.pipe(
				tap((courses) => {
					const courseImages = {};
					courses?.forEach((course) => {
						courseImages[course.alias] = {
							iconUrl: course.uiConfigurations?.iconUrl,
							name: course.name,
						};
					});
					this.dataService.courseImages$.next(courseImages);
				}),
				catchError(() => of(null)),
			)
			.subscribe(() => {});
		this.dataService.courseImages$.next({});
	};

	private initData = (
		initialChatViewState: ChatViewState,
		systemPromptId: string,
	) => {
		this.dataService.chatViewState$.next(initialChatViewState);
		this.dataService.systemPromptId$.next(systemPromptId);

		if (window?.location.pathname !== aIChatPagePath) {
			this.dataService.messages$.next(initialAssistantMessages);
			this.dataService.assistantResponseErrorStatus$.next(null);
			this.dataService.isAssistantResponseLoading$.next(false);
		}
	};

	public getAIResponse = (userMessage: string) => {
		this.addMessageToChatHistory(userMessage, Role.User);
		this.dataService.chatViewState$.next(ChatViewState.Full);
		this.dataService.assistantResponseErrorStatus$.next(null);
		const conversationId = this.dataService.conversationId$.value;
		const systemPromptId = this.dataService.systemPromptId$.value;
		const messages = this.dataService.messages$.value;

		this.dataService.isAssistantResponseLoading$.next(true);
		this.apiService
			.getAIAssistantResponse(
				conversationId ? messages.slice(-1) : messages,
				conversationId,
				systemPromptId,
				this.dataService.isTestUser$.value,
			)
			.pipe(
				tap((aIAssistantResponse) => {
					this.dataService.assistantResponseErrorStatus$.next(null);
					this.dataService.conversationId$.next(
						aIAssistantResponse.conversationId,
					);

					this.addMessageToChatHistory(
						aIAssistantResponse.messages[0].message,
						Role.Assistant,
						aIAssistantResponse.messages[0].aliases,
						aIAssistantResponse.messages[0].id,
						aIAssistantResponse.usage,
					);
				}),
				catchError((error) => {
					this.addMessageToChatHistory(limitError, Role.Assistant);

					this.dataService.assistantResponseErrorStatus$.next(
						error?.[0]?.code,
					);

					return of(null);
				}),
			)
			.subscribe(() => {
				this.dataService.isAssistantResponseLoading$.next(false);
			});
	};

	public handleAIChatMount = (
		initialChatViewState: ChatViewState,
		systemPromptId: string,
	): Observable<null> => {
		this.setCourseImages();
		this.initData(initialChatViewState, systemPromptId);
		this.trackingService.trackAIChatMount();

		return of(null);
	};

	public handleExpandClick = (): Observable<null> => {
		this.dataService.chatViewState$.next(ChatViewState.Extended);

		return of(null);
	};

	public handleHideClick = (): Observable<null> => {
		this.dataService.chatViewState$.next(ChatViewState.Closed);

		return of(null);
	};

	public handleRateClick = (
		rating: Ratings,
		messageId: string,
	): Observable<null> => {
		this.dataService.messages$.next(
			this.dataService.messages$.value.map((message) => (message.id === messageId ? { ...message, rating } : message)),
		);
		this.apiService
			.rateMessage(messageId, rating)
			.pipe(
				catchError(() => of(null)),
			)
			.subscribe();

		return of(null);
	};

	public handleCourseClick = (alias: string): Observable<null> => {
		this.trackingService.trackCourseClick(alias);

		return of(null);
	};
}
